import {
  availableHanziScript,
  useTraditionalCharactersEnabled,
} from "@-/dev.improve.haniverse.lib.posthog";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import ensureError from "@-/com.npmjs.ensure-error";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { getTaiwanUsageMap } from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import { createUserCard } from "@-/dev.improve.haniverse.lib.cards.user-card.create";
import { useClearActorDeck } from "@-/dev.improve.haniverse.lib.cards.deck.clear";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import arrayLength from "@-/com.npmjs.extra-array__length";

export type SeedAllStudiedActorDeckResult = {
  /** Number of graphemes that actually got a card (a meaning card existed). */
  seededCount: number;
  /** Total distinct level graphemes we attempted to seed. */
  totalCount: number;
};

// A `createUserCard` call throws with this message when the grapheme has no
// authored meaning card in the content DB. A handful of level graphemes are
// pure radicals/strokes that never got a card, so we skip those rather than
// aborting the whole (multi-thousand card) seed.
const NO_MEANING_CARD_ERROR_PREFIX = "No meaning card found for front";
const DAY_MS = 24 * 60 * 60 * 1000;
const DUE_SOON_DAYS = 3;
const DUE_TODAY_GRAPHEME_COUNT = 5;

/**
 * Seeds the actor deck from the graphemes studied across every lesson in every
 * level for the user's current character script (`hanziScript`). Pass
 * `graphemeLimit` to create a bounded, repeatable stress-test deck; omit it to
 * seed the whole authored curriculum.
 *
 * The character set is the union of `level.characters` across all levels (the
 * same sequence {@link nextLessonQueryFn} walks), de-duplicated. Each grapheme
 * is introduced via the production `createUserCard` path. A small initial
 * cohort is due today and the rest is due in three days, recreating the long
 * Due Soon accordion seen in an established staging deck. Existing review
 * state is wiped first via {@link useClearActorDeck}.
 */
export function useSeedAllStudiedActorDeck({ graphemeLimit }: { graphemeLimit?: number } = {}) {
  const clearActorDeckMutation = useClearActorDeck();
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();

  return useMutation({
    mutationFn: async (_, { client }): Promise<SeedAllStudiedActorDeckResult> => {
      // Wipe existing review state so the seed starts from a known-empty deck.
      await clearActorDeckMutation.mutateAsync();

      // Resolve the active character script from the actor user settings (now on
      // the `user` row; query-cache backed, same source `nextLessonQueryFn`
      // reads), so the seed matches the levels the user actually studies.
      const { user } = await client.fetchQuery(actorUserDataQueryOptions);
      const hanziScript = availableHanziScript(user.hanziScript, traditionalCharactersEnabled);
      const levels = hanziScript === "traditional" ? getTraditionalLevels() : getSimplifiedLevels();
      const allCharacters = uniq(levels.flatMap((level) => level.characters));
      const charactersToSeed =
        graphemeLimit === undefined ? allCharacters : allCharacters.slice(0, graphemeLimit);

      // Resolved synchronously from the precomputed static-DB build asset; the
      // same value `useCreateUserCard` passes to `createUserCard`.
      const twUsageMap = getTaiwanUsageMap();
      const nowMs = Date.now();
      const dueTodayIso = new Date(nowMs).toISOString();
      const dueSoonIso = new Date(nowMs + DUE_SOON_DAYS * DAY_MS).toISOString();

      let seededCount = 0;
      // Sequential: each `createUserCard` opens its own transaction, and a
      // large deck is a one-off admin action, so keep it deterministic and avoid
      // hammering the actor DB with hundreds or thousands of concurrent writes.
      for (const grapheme of charactersToSeed) {
        try {
          await createUserCard({
            actorDb,
            contentDb,
            graphemeCard: { front: grapheme },
            hanziScript,
            initialFsrsSchedule:
              seededCount < DUE_TODAY_GRAPHEME_COUNT
                ? { due: dueTodayIso, scheduledDays: 0 }
                : { due: dueSoonIso, scheduledDays: DUE_SOON_DAYS },
            // Everything studied: a meaning-only level's characters get their pronunciation
            // cards too, as if the pronunciation level were done.
            scope: "all",
            twUsageMap,
          });
          seededCount += 1;
        } catch (error) {
          const ensured = ensureError(error);
          if (ensured.message.startsWith(NO_MEANING_CARD_ERROR_PREFIX)) {
            // No authored card for this level grapheme — skip it, keep seeding.
            continue;
          }
          throw ensured;
        }
      }

      logger(import.meta.location).info(
        `[seed-all-studied] seeded ${seededCount}/${arrayLength(charactersToSeed)} graphemes ` +
          `for ${hanziScript} script`,
      );

      return { seededCount, totalCount: arrayLength(charactersToSeed) };
    },
    mutationKey: ["seedAllStudiedActorDeck"],
  });
}
